/**
 * Portal Layout — GE Lines Self-Service Portal
 *
 * Authenticated sidebar layout for customer portal pages.
 * Same concept as v1 PortalLayout, adapted for the GE Lines brand.
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Truck,
  Warehouse,
  Receipt,
  FolderOpen,
  Users,
  LogOut,
  Bell,
  ChevronRight,
  User,
  Building2,
  Workflow,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useStorefront } from '@/context/StorefrontContext';
import { TenantBrandMark } from '@/components/TenantBrandMark';
import { resolvePortalTheme } from '@/lib/portal-theme';
import { canUsePortalPermission, type PortalPermission } from '@/lib/portal-access';
import { API_BASE } from '@/lib/api-base';

const sidebarLinks: Array<{
  href: string;
  label: string;
  icon: typeof Home;
  permission: PortalPermission;
  menuKey: string;
}> = [
  { href: '/portal', label: 'Overview', icon: Home, permission: 'portal:overview:read', menuKey: 'overview' },
  { href: '/portal/trucking', label: 'Trucking', icon: Truck, permission: 'portal:operations:read', menuKey: 'trucking' },
  { href: '/portal/depot', label: 'Depot', icon: Building2, permission: 'portal:operations:read', menuKey: 'depot' },
  { href: '/portal/warehouse', label: 'Warehouse', icon: Warehouse, permission: 'portal:operations:read', menuKey: 'warehouse' },
  { href: '/portal/bills-payments', label: 'Bills & Payments', icon: Receipt, permission: 'portal:finance:read', menuKey: 'bills-payments' },
  { href: '/portal/documents-reports', label: 'Documents & Reports', icon: FolderOpen, permission: 'portal:documents:read', menuKey: 'documents-reports' },
  { href: '/portal/users', label: 'Users', icon: Users, permission: 'portal:users:manage', menuKey: 'users' },
];

const breadcrumbLabels: Record<string, string> = {
  '/portal': 'Overview',
  '/portal/tm': 'Transport Portal',
  '/portal/trucking': 'Trucking',
  '/portal/depot': 'Depot',
  '/portal/warehouse': 'Warehouse',
  '/portal/bills-payments': 'Bills & Payments',
  '/portal/documents-reports': 'Documents & Reports',
  '/portal/users': 'Users',
};

export function PortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, accessToken, logout } = useAuth();
  const { brand, visual, host } = useStorefront();
  const portalTheme = resolvePortalTheme(brand.portalThemeTemplate, visual);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [enabledMenuKeys, setEnabledMenuKeys] = useState<Set<string>>(new Set(['overview']));
  const [runtimePortalLinks, setRuntimePortalLinks] = useState<Array<{
    href: string;
    label: string;
    menuKey: string;
    moduleId: string;
  }>>([]);
  const tenantDisplayName = user?.tenantName ?? brand.legalName;
  const visibleSidebarLinks = sidebarLinks.filter(link => (
    canUsePortalPermission(user?.role, link.permission, user?.portalPermissions)
    && enabledMenuKeys.has(link.menuKey)
  ));

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const loadMenu = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/selfservice/service-portal`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: AbortSignal.timeout(8000),
        });
        const envelope = await res.json() as {
          ok: boolean;
          data?: { menu?: Array<{ key: string; enabled: boolean }> };
        };
        if (!res.ok || !envelope.ok || !envelope.data?.menu) throw new Error('Menu unavailable');
        const keys = new Set(envelope.data.menu.filter(item => item.enabled).map(item => item.key));
        if (!cancelled) setEnabledMenuKeys(keys);
      } catch {
        if (!cancelled) setEnabledMenuKeys(new Set(['overview']));
      }
    };
    void loadMenu();
    return () => { cancelled = true; };
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const loadRuntimePortalNavigation = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/dynamic-runtime/module/profiles`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: AbortSignal.timeout(8000),
        });
        const envelope = await res.json() as {
          ok: boolean;
          data?: {
            profiles?: Array<{
              profileId: string;
              moduleId: string;
              label: string;
              routes?: string[];
            }>;
          };
        };
        if (!res.ok || !envelope.ok) throw new Error('Runtime profiles unavailable');
        const links = (envelope.data?.profiles ?? [])
          .flatMap((profile) => {
            const portalRoute = (profile.routes ?? []).find((route) => route.startsWith('/portal/'));
            return portalRoute ? [{
              href: portalRoute,
              label: profile.label,
              menuKey: `runtime:${profile.profileId}`,
              moduleId: profile.moduleId,
            }] : [];
          })
          .sort((left, right) => {
            const order = new Map(['tm', 'ct', 'wm', 'fm', 'acc'].map((moduleId, index) => [moduleId, index]));
            return (order.get(left.moduleId) ?? 99) - (order.get(right.moduleId) ?? 99);
          });
        if (!cancelled) setRuntimePortalLinks(links);
      } catch {
        if (!cancelled) setRuntimePortalLinks([]);
      }
    };
    void loadRuntimePortalNavigation();
    return () => { cancelled = true; };
  }, [accessToken]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="relative min-h-screen flex" style={{ ...portalTheme.cssVars, ...portalTheme.shellBackground }}>
      <div className="pointer-events-none absolute inset-0" style={portalTheme.textureOverlay} />
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="relative z-10 w-64 border-r border-white/50 flex flex-col shrink-0" style={portalTheme.sidebarSurface}>
        {/* Logo */}
        <div className="min-h-20 flex items-center px-5 py-4 border-b border-slate-200">
          <Link to="/" className="min-w-0">
            <TenantBrandMark
              brand={brand}
              size="md"
              subtitle={brand.portalLabel}
              detail={tenantDisplayName}
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5">
          {runtimePortalLinks.map(link => {
            const isActive = location.pathname === link.href || location.pathname.startsWith(`${link.href}/`);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Workflow className="w-4.5 h-4.5" />
                {link.label}
              </Link>
            );
          })}
          {visibleSidebarLinks.map(link => {
            const Icon = link.icon;
            const isActive =
              link.href === '/portal'
                ? location.pathname === '/portal'
                : location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="w-4.5 h-4.5" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 pb-4">
          <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white p-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-700">
              <Building2 className="w-3.5 h-3.5" />
              Tenant Workspace
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-900">{tenantDisplayName}</p>
            <p className="mt-1 text-xs text-slate-500 break-all">{host}</p>
          </div>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name ?? 'User'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.customerName ?? user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main area ────────────────────────────────────────────────────── */}
      <div className="relative z-10 flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-white/50 flex items-center justify-between px-6 shrink-0" style={portalTheme.panelSurface}>
          {/* Breadcrumb */}
          <div className="flex items-center gap-4 min-w-0">
            <nav className="flex items-center gap-1.5 text-sm text-slate-500 min-w-0">
              <Link to="/portal" className="hover:text-brand-600 transition-colors">Portal</Link>
              {location.pathname !== '/portal' && (
                <>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                  <span className="text-slate-900 font-medium capitalize truncate">
                    {breadcrumbLabels[location.pathname] ?? location.pathname.split('/').pop()}
                  </span>
                </>
              )}
            </nav>
            <div className="hidden md:flex items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-3 py-1 text-xs text-brand-700">
              <Building2 className="w-3.5 h-3.5" />
              <span className="font-semibold">{brand.brandShortName}</span>
              <span className="text-brand-500">workspace</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button className="p-2 text-slate-400 hover:text-slate-700 relative rounded-lg hover:bg-slate-50">
              <Bell className="w-5 h-5" />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
                </div>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showUserMenu ? 'rotate-90' : 'rotate-0'}`} />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50">
                  <div className="px-4 py-2 border-b border-slate-100 mb-1">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                    <p className="text-[11px] text-slate-500 truncate mt-1">{tenantDisplayName} · {user?.role ?? 'viewer'}</p>
                  </div>
                  <Link
                    to="/portal/users"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                  >
                    <User className="w-4 h-4" /> Profile & Users
                  </Link>
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
